<script lang="ts">
    import { link, pop } from 'svelte-spa-router';
    import { buildOfficialMessageUrl } from '../../api/bills';
    import { getProfiles } from '../../api/elections';
    import type { Profile as OfficialProfile } from '../../api/officials';
    import OfficialProfileSkeleton from '../../lib/Bills/OfficialProfileSkeleton.svelte';
    import ProfileView from '../Directory/Profile.svelte';
    import { billsLastBillUri } from './billsState';
    import * as t from '../../translations/Bills.ts';

    /** `id` is the sponsor's office occupancy id, the same id the directory uses for elected officials. */
    export let params: { id?: string } = {};

    let loading = true;
    let profile: OfficialProfile | null = null;
    let loadFailed = false;
    let requestedId: string | undefined;
    const cameFromBill = !!billsLastBillUri.value;

    $: void load(params.id);

    async function load(id: string | undefined) {
        requestedId = id;
        loading = true;
        profile = null;
        loadFailed = false;
        try {
            if (!id) return;
            const [loaded] = await getProfiles([{ id: Number(id), type: 'E' }]);
            if (requestedId === id) profile = loaded ?? null;
        } catch (e) {
            console.error('[Bills] Failed to load official profile:', e);
            if (requestedId === id) loadFailed = true;
        } finally {
            if (requestedId === id) loading = false;
        }
        window.scrollTo?.({ top: 0 });
    }

    function sendMessage() {
        if (params.id) window.location.assign(buildOfficialMessageUrl(Number(params.id)));
    }

    const backClass =
        'mb-6 inline-flex items-center gap-2 rounded-md !border-0 !bg-transparent !px-1.5 !py-1 !text-gray-600 transition-colors hover:!text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--vv-action-center-button-color,#2563eb)] focus-visible:ring-offset-2';
</script>

<div class="bills-official-profile @container w-full m-auto max-w-[1000px] py-4 px-4 md:px-8 interfont" data-testid="official-profile-page">
    {#if cameFromBill}
        <button type="button" class={backClass} on:click={() => pop()}>
            <svg class="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
            <span class="text-base font-medium underline-offset-2 hover:underline">{t.Back_To_Bill()}</span>
        </button>
    {:else}
        <a href="/" use:link class={backClass}>
            <svg class="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
            <span class="text-base font-medium underline-offset-2 hover:underline">{t.Back_To_Bills()}</span>
        </a>
    {/if}

    {#if loading}
        <div aria-busy="true" aria-label={t.Loading()}>
            <OfficialProfileSkeleton />
        </div>
    {:else if profile}
        {#key params.id}
            <div class="flex flex-col grid-cols-6 @xl:grid gap-4">
                <ProfileView
                    params={{ id: params.id }}
                    getPreviousRoute={undefined}
                    {profile}
                    showBackButton={false}
                    onSendMessage={sendMessage}
                />
            </div>
        {/key}
    {:else if loadFailed}
        <div class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
            {t.Official_Load_Error()}
        </div>
    {:else}
        <p class="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700" data-testid="official-profile-unavailable">
            {t.Official_Not_Available()}
        </p>
    {/if}
</div>
